import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import {
	Box,
	Button,
	Combobox,
	Group,
	LoadingOverlay,
	Modal,
	Stack,
	Table,
	Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedRowModel,
	createFilteredRowModel,
	createPaginatedRowModel,
	createSortedRowModel,
	flexRender,
	useTable,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import log from 'loglevel';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AddIcon, ClearIcon, DragIcon, ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { createDataTableColumnHelper } from '@apple/ui/data-table/utils/column-helper';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { adminAutocomplete } from '@apple/features/product/api/listing';
import { ProductAutocomplete } from '@apple/features/product/components/ProductAutocomplete';
import type {
	AdminProductAutocompleteRequest,
	ProductAutoCompleteModel,
} from '@apple/features/product/models/management';
import type { ProductKey } from '@apple/features/product/models/product';
import {
	addFeaturedProduct,
	deleteFeaturedProduct,
	updateFeaturedProductOrder,
} from '@apple/features/product-retail/api/featured';
import type {
	FeaturedProduct,
	FeaturedProductFilters,
} from '@apple/features/product-retail/models/management';
import {
	productQueryKeys,
	retailProductQueryOptions,
} from '@apple/features/product-retail/queries/management';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
});

export const Route = createFileRoute('/_authed/_admin/manage/featured-products')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.FeaturedProductManagement.Read',
		});
	},
	component: FeaturedProductsListRoute,
});

const MAX_FEATURED_PRODUCTS = 50;

function FeaturedProductsListRoute() {
	const { queryClient } = Route.useRouteContext();
	const { t } = useTranslation('manage');
	const navigate = Route.useNavigate();
	const search = Route.useSearch();
	const [rows, setRows] = useState<FeaturedProduct[]>([]);
	const [isUpdating, setIsUpdating] = useState(false);
	const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
	const [selectedProduct, setSelectedProduct] = useState<ProductAutoCompleteModel | undefined>();
	const columnHelper = createDataTableColumnHelper<FeaturedProduct, FeaturedProductFilters>();

	const handleModalClose = () => {
		setSelectedProduct(undefined);
		closeModal();
	};

	const { data: queryData, isFetching } = useQuery(retailProductQueryOptions.featured());

	useEffect(() => {
		if (queryData) {
			setRows(queryData);
		}
	}, [queryData]);

	const defaultFilters: FeaturedProductFilters = {};

	const tableState = useTableState<FeaturedProduct, FeaturedProductFilters>({
		search,
		navigate,
		defaultFilters,
		defaultSorting: [],
		fieldMap: [],
		defaultPageSize: 100,
	});

	const columns = useMemo(
		() =>
			columnHelper.columns([
				columnHelper.display({
					id: 'drag',
					header: '',
					cell: () => null,
					size: 10,
				}),
				columnHelper.accessor('productId', {
					header: t('products.fields.sku'),
					cell: info => info.getValue(),
					filter: {
						group: t('products.filterGroups.keywordSearch'),
					},
				}),
				columnHelper.accessor('title', {
					header: t('products.fields.productName'),
					cell: info => info.getValue(),
					filter: {
						group: t('products.filterGroups.keywordSearch'),
					},
				}),
				columnHelper.accessor('createdOn', {
					header: t('featuredProducts.fields.createdOn'),
					cell: ({ row }) => <FormattedDate value={row.original.createdOn} />,
					filterFn: (row, _columnId, filterValue) => {
						if (!filterValue || tableState.currentFilterData.createdOn === undefined)
							return true;

						const maxDate = dayjs(filterValue as string);
						const createdOn = dayjs(row.original.createdOn);
						return createdOn.isSame(maxDate, 'day');
					},
					filter: {
						group: t('products.filterGroups.keywordSearch'),
						variant: 'date',
					},
				}),
				columnHelper.display({
					id: 'remove',
					header: '',
					cell: () => null,
					size: 10,
				}),
			]),
		[t, columnHelper, tableState.currentFilterData.createdOn],
	);

	const table = useTable({
		features,
		data: rows ?? [],
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: false,
		manualPagination: true,
		columns: columns,
		columnResizeMode: 'onChange',
	});

	const handleDragEnd = useCallback(
		async (result: DropResult) => {
			const { source, destination } = result;
			if (!destination || source.index === destination.index) {
				return;
			}

			const updated = Array.from(rows);
			const [moved] = updated.splice(source.index, 1);
			updated.splice(destination.index, 0, moved!);

			const reNumbered = updated.map((r, idx) => ({
				...r,
				sortOrder: idx + 1,
			}));

			setRows(reNumbered);

			try {
				setIsUpdating(true);
				for (const row of reNumbered) {
					if (
						row.sortOrder !== rows.find(r => r.productId === row.productId)?.sortOrder
					) {
						await updateFeaturedProductOrder(row.productId, row.sortOrder);
						await queryClient.invalidateQueries({
							queryKey: productQueryKeys.featured,
						});
					}
				}
			} catch (err) {
				log.error('Error updating featured sort order:', err);
			} finally {
				setIsUpdating(false);
			}
		},
		[rows, queryClient.invalidateQueries],
	);

	function renderAutocompleteOptions(products: ProductAutoCompleteModel[]) {
		return products.map(option => (
			<Combobox.Option value={option.id} key={option.id}>
				<Group gap='sm'>{option.displayString}</Group>
			</Combobox.Option>
		));
	}

	async function handleAutocomplete(searchText: string, excludedProducts: string[]) {
		const request: AdminProductAutocompleteRequest = {
			searchText: searchText,
			partNumbers: excludedProducts,
			warehouseLocationId: null,
		};

		return await adminAutocomplete(request);
	}

	const handleAddProduct = async () => {
		try {
			await addFeaturedProduct(selectedProduct?.id ?? '');
			await queryClient.invalidateQueries({
				queryKey: productQueryKeys.featured,
			});
			notifications.show({
				message: t('featuredProducts.productAdded'),
				icon: <Success />,
				color: 'green',
				autoClose: 5000,
			});
		} catch {
			notifications.show({
				color: 'red',
				message: t('common:error.generic'),
				icon: <ErrorIcon />,
			});
		} finally {
			handleModalClose();
		}
	};

	const handleDeleteProduct = (productId: ProductKey) => {
		modals.openConfirmModal({
			title: t('featuredProducts.removeProduct.title'),
			labels: {
				confirm: t('common:buttons.confirm'),
				cancel: t('common:buttons.cancel'),
			},
			onConfirm: async () => {
				try {
					await deleteFeaturedProduct(productId);
					await queryClient.invalidateQueries({
						queryKey: productQueryKeys.featured,
					});
					notifications.show({
						message: t('featuredProducts.productDeleted'),
						icon: <Success />,
						color: 'green',
						autoClose: 5000,
					});
				} catch {
					notifications.show({
						color: 'red',
						message: t('common:error.generic'),
						icon: <ErrorIcon />,
					});
				} finally {
					handleModalClose();
				}
			},
			children: <Text>{t('featuredProducts.removeProduct.message')}</Text>,
		});
	};

	return (
		<TableLayout
			title={t('featuredProducts.title')}
			table={table}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('featuredProducts.buttons.add')}
					icon={AddIcon}
					onClick={openModal}
					disabled={rows.length >= MAX_FEATURED_PRODUCTS}
				/>,
			]}
			stackJustify='flex-start'
		>
			{!isFetching && rows.length > 0 && (
				<Stack>
					<Group justify='space-between' pt='xs' px='xs'>
						<Text size='sm' c='dimmed'>
							{t('featuredProducts.description')}
						</Text>
						<Text fw={700}>
							{t('featuredProducts.productCount', { count: rows.length })}
						</Text>
					</Group>

					<Box pos='relative'>
						<LoadingOverlay visible={isUpdating} overlayProps={{ blur: 2 }} />

						<DragDropContext onDragEnd={handleDragEnd}>
							<Table variant='apple-table'>
								<Table.Thead>
									{table.getHeaderGroups().map(headerGroup => (
										<Table.Tr key={headerGroup.id}>
											{headerGroup.headers.map(header => (
												<Table.Th key={header.id}>
													{flexRender(
														header.column.columnDef.header,
														header.getContext(),
													)}
												</Table.Th>
											))}
										</Table.Tr>
									))}
								</Table.Thead>

								<Droppable droppableId='featured-products-body'>
									{provided => (
										<Table.Tbody
											ref={provided.innerRef}
											{...provided.droppableProps}
										>
											{table.getRowModel().rows.map((row, rowIndex) => (
												<Draggable
													key={row.original.productId}
													draggableId={row.original.productId}
													index={rowIndex}
												>
													{dragProvided => (
														<Table.Tr
															ref={dragProvided.innerRef}
															{...dragProvided.draggableProps}
															{...dragProvided.dragHandleProps}
														>
															<Table.Td>
																<DragIcon c='dimmed' />
															</Table.Td>
															{row
																.getVisibleCells()
																.filter(
																	x =>
																		!x.id.endsWith('drag') &&
																		!x.id.endsWith('remove'),
																)
																.map(cell => (
																	<Table.Td key={cell.id}>
																		{flexRender(
																			cell.column.columnDef
																				.cell,
																			cell.getContext(),
																		)}
																	</Table.Td>
																))}
															<Table.Td>
																<ClearIcon
																	tooltip={t(
																		'product:buttons.delete',
																	)}
																	c='red'
																	onClick={() =>
																		handleDeleteProduct(
																			row.original.productId,
																		)
																	}
																	style={{ cursor: 'pointer' }}
																/>
															</Table.Td>
														</Table.Tr>
													)}
												</Draggable>
											))}
											{provided.placeholder}
										</Table.Tbody>
									)}
								</Droppable>
							</Table>
						</DragDropContext>
					</Box>
				</Stack>
			)}
			<Modal
				id='project-dialog'
				title={t('featuredProducts.addProduct')}
				opened={modalOpened}
				onClose={closeModal}
				closeOnClickOutside={false}
				size='lg'
			>
				<Stack>
					<ProductAutocomplete<ProductAutoCompleteModel>
						onSearch={handleAutocomplete}
						renderOptions={renderAutocompleteOptions}
						keySelector={x => x.id}
						displayTextSelector={x => x.displayString}
						excludedPartNumbers={rows.map(r => r.productId)}
						value={selectedProduct}
						onChange={setSelectedProduct}
					/>
					<Group justify='flex-end'>
						<Button color={'red'} onClick={handleModalClose}>
							{t('common:buttons.cancel')}
						</Button>
						<Button disabled={!selectedProduct} onClick={handleAddProduct}>
							{t('featuredProducts.buttons.add')}
						</Button>
					</Group>
				</Stack>
			</Modal>
		</TableLayout>
	);
}
